import { useCallback } from 'react';

import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { getMostPermissiveUserStorableRole } from 'src/helpers/accounts';

import type { LookupDocxUploadDocument } from './resolve-docx-upload-target';

export function useDocxUploadDocumentLookup(): LookupDocxUploadDocument {
  const currentUserId = useCurrentUserId();
  const [getDocxUploadTargetDocument] = Apollo.useGetDocxUploadTargetDocumentLazyQuery({
    fetchPolicy: 'network-only',
  });

  return useCallback<LookupDocxUploadDocument>(
    async documentId => {
      if (!currentUserId) return null;

      const { data } = await getDocxUploadTargetDocument({
        variables: { documentIds: [documentId] },
      });

      const document = data?.documents?.find(doc => doc?.id === documentId);
      if (!document) return null;

      const { deletedAt, id, isSigned, name, owner, requesterRole, workflow } = document;
      if (
        deletedAt ||
        !id ||
        isSigned === undefined ||
        !name ||
        owner?.__typename !== 'User' ||
        !owner.id
      )
        return null;

      const { isEditor, isOwner } = getMostPermissiveUserStorableRole(currentUserId, {
        __typename: 'Document',
        owner: { __typename: 'User', id: owner.id },
        requesterRole: requesterRole ?? null,
      });

      return {
        canEdit: isOwner || isEditor,
        id,
        isSigned,
        name,
        workflow:
          workflow?.__typename === 'Workflow' && workflow.state
            ? { __typename: 'Workflow', state: workflow.state }
            : null,
      };
    },
    [currentUserId, getDocxUploadTargetDocument]
  );
}
